import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";

export const HeroSection = () => {
  const [showContent, setShowContent] = useState(false);
  const [showCTA, setShowCTA] = useState(false);
  const [portalActive, setPortalActive] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    // Kick off video playback and staggered content fade-in
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
    const t1 = setTimeout(() => setShowContent(true), 600);
    const t2 = setTimeout(() => setShowCTA(true), 1600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const scrollToNextSection = () => {
    // Trigger the cinematic black-fade portal threshold (~500ms in, hold, ~500ms out)
    setPortalActive(true);

    // Begin the smooth scroll while the overlay is at full opacity so the
    // products are already coming into view as it fades out.
    const scrollTimer = setTimeout(() => {
      const shopSection = document.getElementById("featured-drops");
      if (shopSection) {
        shopSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 500);

    // Fade the overlay back out once the user is crossing the threshold
    const fadeOutTimer = setTimeout(() => setPortalActive(false), 1100);

    // Cleanup defensively if component unmounts before timers fire
    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(fadeOutTimer);
    };
  };

  return (
    <section
      data-testid="hero-section"
      className="relative w-full overflow-hidden bg-black"
      style={{ minHeight: "100vh" }}
    >
      {/* Single looping hero video — same-origin, H.264 MP4 */}
      <div className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }}>
        <video
          ref={videoRef}
          data-testid="hero-video"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/assets/images/cream/cream-set.png"
          className="w-full h-full object-cover"
          style={{ objectPosition: "center" }}
        >
          <source src="/assets/videos/hero-portal.mp4" type="video/mp4" />
        </video>

        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* Hero Content */}
      <div
        className={`absolute inset-0 flex items-center justify-center transition-opacity duration-1000 ${
          showContent ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{ zIndex: 5 }}
      >
        <div className="relative text-center px-6 md:px-12 max-w-5xl mx-auto">
          <h1
            data-testid="hero-headline"
            className={`text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight drop-shadow-2xl transition-all duration-1000 ${
              showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            UNIXORN
          </h1>
          <p
            data-testid="hero-tagline"
            className={`text-xl md:text-2xl text-white/90 mb-10 tracking-wide drop-shadow-lg transition-all duration-1000 delay-300 ${
              showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            Each UNIXORN set exists once — then becomes legend.
          </p>
          <div
            className={`transition-all duration-1000 ${
              showCTA ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <Button
              data-testid="enter-portal-btn"
              onClick={scrollToNextSection}
              variant="outline"
              className="px-10 py-6 text-base md:text-lg bg-transparent border-2 border-white/40 text-white hover:bg-white hover:text-black hover:border-white transition-all duration-500 rounded-none uppercase tracking-widest font-semibold shadow-2xl"
            >
              Enter The Portal
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce transition-opacity duration-1000 ${
          showCTA ? "opacity-40" : "opacity-0"
        }`}
        style={{ zIndex: 5 }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-white/30 rounded-full" />
        </div>
      </div>

      {/* Cinematic portal threshold overlay — fades to full black for ~0.5s
          while the page smooth-scrolls, then fades back out as products
          appear. Pointer-events disabled so it never blocks interaction. */}
      <div
        data-testid="portal-overlay"
        aria-hidden="true"
        className={`fixed inset-0 bg-black pointer-events-none transition-opacity ease-in-out ${
          portalActive ? "opacity-100 duration-500" : "opacity-0 duration-700"
        }`}
        style={{ zIndex: 9999 }}
      />
    </section>
  );
};

export default HeroSection;
